import { useApiClient } from "@/lib/api/api.client";
import type { Permission } from "@soulbyte/core";
import { useIsEntitled } from "@/lib/auth/permissions/permissions";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { sortThreadsByRecency, type Thread } from "./threads.utils";

// =============================================================================
// GET /threads/{assistant_id}/conversations
//
// The single source for the assistant thread list: the caller's own chats plus
// every website (widget) and phone conversation for the assistant, each tagged
// with `source`. One call powers the whole list; the UI filters and badges by
// channel client-side. Access is gated server-side by the assistant-access
// check; here the thread.list entitlement is the UI affordance.
// =============================================================================

export function useConversations({ assistantId }: { assistantId: string }) {
  const apiClient = useApiClient();
  const isEntitled = useIsEntitled("thread.list");

  const query = apiClient.useQuery(
    "get",
    "/threads/{assistant_id}/conversations",
    { params: { path: { assistant_id: assistantId } } },
    { enabled: isEntitled && !!assistantId },
  );

  const _threads = query.data?.data?.threads;
  const threads = useMemo(
    () =>
      _threads?.length ? sortThreadsByRecency(_threads) : (_threads ?? []),
    [_threads],
  );

  return Object.assign({}, query, {
    threads,
    isEntitled,
    permissions: ["thread.list"] satisfies Permission[],
  });
}

export function useInvalidateConversations() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return function ({ assistantId }: { assistantId: string }) {
    const queryOptions = apiClient.queryOptions(
      "get",
      "/threads/{assistant_id}/conversations",
      { params: { path: { assistant_id: assistantId } } },
    );

    return queryClient.invalidateQueries(queryOptions);
  };
}

export type { Thread };
